import { __rest } from "tslib";
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { createPortal } from 'react-dom';
import useKeyPress from '../../hooks/use-keypress';
import keyCodes from '../../utils/keycodes';
import { useDropdown } from '../dropdown';
import { Dropdown } from '../dropdown/dropdown';
import { context } from './utils';
import s from './select.module.css';
const keys = [keyCodes.up, keyCodes.down, keyCodes.space, keyCodes.enter];
const allOptions = `[data-option]:not([data-disabled])`;
const focusedOption = `[data-option][data-focus]`;
const hoveredOption = `[data-option]:hover:not([data-disabled])`;
const selectedOption = `[data-option][data-selected]`;
function focus(children, i) {
    children.forEach(el => {
        delete el.dataset.focus;
    });
    if (i === null) {
        return;
    }
    if (children[i]) {
        children[i].dataset.focus = 'true';
    }
}
function keyboard(container, state) {
    delete container.dataset.keyboard;
    if (!state) {
        return;
    }
    container.dataset.keyboard = 'true';
}
const Content = (_a) => {
    var { children, dataTest: externalDataTest, isSearch } = _a, props = __rest(_a, ["children", "dataTest", "isSearch"]);
    const { opened } = useDropdown();
    const { mode, search, registerSearchInput, dataTest: contextDataTest } = useContext(context);
    const listRef = useRef(null);
    const searchRef = useRef(null);
    const getOptions = useCallback(() => { var _a; return Array.from(((_a = listRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(allOptions)) || []); }, []);
    const handleMouseMove = useCallback(() => {
        var _a;
        if (!((_a = listRef.current) === null || _a === void 0 ? void 0 : _a.dataset.keyboard)) {
            return;
        }
        keyboard(listRef.current, false);
        const focused = listRef.current.querySelector(focusedOption);
        delete focused.dataset.focus;
    }, []);
    useEffect(() => {
        if (listRef.current && !opened) {
            focus(getOptions(), null);
        }
    }, [opened, getOptions]);
    useEffect(() => {
        if (!searchRef.current) {
            return;
        }
        registerSearchInput(searchRef.current);
    }, [registerSearchInput, search.show]);
    useKeyPress(keys, event => {
        const children = getOptions();
        const count = children.length;
        let current;
        let i;
        if (!opened || count === 0 || !listRef.current) {
            return;
        }
        current = listRef.current.querySelector(focusedOption);
        const input = searchRef.current && searchRef.current.querySelector('input');
        if (!current) {
            current = listRef.current.querySelector(hoveredOption);
        }
        if (!current && mode === 'single') {
            current = listRef.current.querySelector(selectedOption);
        }
        i = children.findIndex(node => node === current);
        switch (event.keyCode) {
            case keyCodes.up:
                event.preventDefault();
                i = current ? (i - 1 + count) % count : count - 1;
                focus(children, i);
                keyboard(listRef.current, true);
                break;
            case keyCodes.down:
                event.preventDefault();
                i = current ? (i + 1) % count : 0;
                focus(children, i);
                keyboard(listRef.current, true);
                break;
            case keyCodes.enter:
                if (children[i] && (document.activeElement !== input || isSearch)) {
                    event.preventDefault();
                    children[i].click();
                    if (mode !== 'standalone') {
                        focus(children, i);
                    }
                    if (mode !== 'multiple') {
                        keyboard(listRef.current, false);
                    }
                }
                break;
        }
    });
    return (React.createElement(Dropdown.Content, Object.assign({ padding: false, flex: true, dataTest: contextDataTest ? `${contextDataTest}-content` : externalDataTest }, props),
        search.show && React.createElement("div", { ref: searchRef, className: s.search }),
        React.createElement("div", { ref: listRef, className: classNames(s.wrapper, mode !== 'multiple' && s.single), onMouseMove: handleMouseMove }, children)));
};
const ContentNative = ({ children }) => {
    const { select } = useContext(context);
    if (!select) {
        return null;
    }
    return createPortal(React.createElement(React.Fragment, null, children), select);
};
const SelectContent = (_a) => {
    var { children } = _a, props = __rest(_a, ["children"]);
    const { native } = useContext(context);
    return native ? React.createElement(ContentNative, null, children) : React.createElement(Content, Object.assign({}, props), children);
};
export default SelectContent;
//# sourceMappingURL=content.js.map